import { Link } from './nav'
import { useState } from 'react'
import { ArrowRight, Clock3, FileWarning, GitCommitHorizontal } from 'lucide-react'
import { useGetPlaywrightAnalysesQuery } from '../services/projectDataApi'
import type { PlaywrightAnalyses } from '../schemas/playwrightAnalyses'
import { playwrightTestIdentity, playwrightErrorText } from '../lib/playwrightFailurePresentation'
import './PlaywrightTestsTab.css'

export function statusLabel(analysis: PlaywrightAnalyses['analyses'][number]) {
  if (analysis.report) return analysis.report.classification === 'unresolved' ? 'Analyzed · unresolved' : 'Analyzed'
  if (analysis.status === 'queued') return 'Queued'
  if (['failed', 'cancelled', 'completed'].includes(analysis.status)) return 'Analysis failed'
  return 'Processing'
}

export function ExternalLink({ url, children }: { url: string; children: React.ReactNode }) {
  // Submitted CI URLs are untrusted; only web links may be navigated from a report.
  if (!/^https?:\/\//i.test(url)) return <span>{children}</span>
  return <a href={url} target="_blank" rel="noopener noreferrer">{children}</a>
}

function errorPreview(error: string) {
  const plain = playwrightErrorText(error)
  return plain.length > 1200 ? `${plain.slice(0, 1200)}…` : plain
}

export default function PlaywrightTestsTab({ projectId }: { projectId: string }) {
  const [offset, setOffset] = useState(0)
  const { currentData: data, isFetching, isError, refetch } = useGetPlaywrightAnalysesQuery({ projectId, offset }, {
    pollingInterval: 15_000, skipPollingIfUnfocused: true,
  })
  const endpoint = `${window.location.origin}/api/v1/projects/${encodeURIComponent(projectId)}/failure-analyses`
  return <section className="playwright-tests" aria-labelledby="playwright-tests-title">
    <header>
      <h2 id="playwright-tests-title" className="project-tab-title">Playwright Tests <span>Experimental</span></h2>
      <p>Root-cause failed Playwright tests using their Replay recordings.</p>
    </header>
    <details className="playwright-tests__setup" open={data?.total === 0}>
      <summary>Connect your CI</summary>
      <ol>
        <li>Record tests with Replay’s Playwright integration and upload failed recordings to Replay. QA’s workers must have access to them.</li>
        <li>Create a QA API token in Settings → API. Its owner must have write access to this project.</li>
        <li>Store the token as <code>QA_ANALYSIS_API_TOKEN</code> and the Replay upload key as <code>REPLAY_API_KEY</code> in your CI secrets. Set <code>QA_ANALYSIS_PROJECT_ID</code> to <code>{projectId}</code>.</li>
        <li>Submit each failed attempt to this project’s endpoint:</li>
      </ol>
      <pre>{`POST ${endpoint}\nAuthorization: Bearer <QA_ANALYSIS_API_TOKEN>\nContent-Type: application/json\n\n${JSON.stringify({ recording_id: '<uploaded Replay recording UUID>', ci_run_url: 'https://github.com/owner/repo/actions/runs/123/attempts/1', commit_sha: '<40-character commit SHA>', test_id: '<stable test ID>', title: 'Onboarding completes', retry: 0, error: '<Playwright error and stack>', source: '<test source and relevant fixtures>', mocked_services: false }, null, 2)}`}</pre>
      <p>The API returns <code>202</code> with an analysis ID and status URL. Repeated submissions of the same attempt reuse its analysis. Each new analysis uses one credit and requires an active project. Progress and results appear here automatically.</p>
    </details>
    {isError && <p role="alert">Could not load Playwright analyses. <button onClick={() => void refetch()}>Retry</button></p>}
    {!data && isFetching && <p role="status">Loading Playwright analyses…</p>}
    {data && <>
      <div className="playwright-tests__counts" role="status">
        <span><strong>{data.total}</strong> submitted</span>
        <span><strong>{data.processing}</strong> processing</span>
        <span><strong>{data.completed}</strong> analyzed</span>
        <span><strong>{data.failed}</strong> failed</span>
      </div>
      {data.total === 0 && <p>No test failures have been submitted to this project yet. Connect CI above to get started.</p>}
      <ul className="playwright-tests__list" aria-label="Test failure analyses">
        {data.analyses.map(analysis => {
          const { testName, testContext } = playwrightTestIdentity(analysis.input.title)
          return <li key={analysis.analysis_id}>
            <Link className="playwright-tests__card" to={`/projects/${projectId}/playwright-tests/${analysis.analysis_id}`}>
              <span className="playwright-tests__icon"><FileWarning size={20} aria-hidden="true" /></span>
              <div className="playwright-tests__content">
                <div className="playwright-tests__card-header">
                  <div className="playwright-tests__test-title">
                    {testContext && <p className="playwright-tests__test-context">{testContext}</p>}
                    <h3>{testName}</h3>
                  </div>
                  <span className="playwright-tests__status" data-status={statusLabel(analysis)}>{statusLabel(analysis)}</span>
                </div>
                <pre className="playwright-tests__error">{errorPreview(analysis.input.error)}</pre>
                <div className="playwright-tests__metadata">
                  <span><Clock3 size={14} aria-hidden="true" /><time dateTime={analysis.created_at}>{new Date(analysis.created_at).toLocaleString()}</time></span>
                  <span title={`Commit ${analysis.input.commit_sha}`}><GitCommitHorizontal size={14} aria-hidden="true" /><span>Commit <code>{analysis.input.commit_sha.slice(0, 7)}</code></span></span>
                  <span>Attempt {analysis.input.retry + 1}</span>
                </div>
              </div>
              <ArrowRight className="playwright-tests__arrow" size={18} aria-hidden="true" />
            </Link>
          </li>
        })}
      </ul>
      {data.total > data.limit && <nav className="playwright-tests__pagination" aria-label="Analysis pages">
        <button disabled={offset === 0 || isFetching} onClick={() => setOffset(Math.max(0, offset - data.limit))}>Previous</button>
        <span>{offset + 1}–{Math.min(offset + data.limit, data.total)} of {data.total}</span>
        <button disabled={offset + data.limit >= data.total || isFetching} onClick={() => setOffset(offset + data.limit)}>Next</button>
      </nav>}
    </>}
  </section>
}
